import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import { useTheme } from '../context/ThemeContext';

function Lading() {
  const { darkMode } = useTheme();
  const [chargers, setChargers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState('');

  useEffect(() => {
    const fetchPrices = async () => {
      const sources = [
        '/api/charging-prices',
      ];

      for (const url of sources) {
        try {
          const res = await fetch(url);
          if (!res.ok) continue;
          const xml = await res.text();

          const parser = new DOMParser();
          const doc = parser.parseFromString(xml, 'text/xml');
          if (doc.querySelector('parsererror')) continue;

          const items = doc.querySelectorAll('item');
          if (items.length === 0) continue;

          const data = Array.from(items).map(item => {
            const titleText = item.querySelector('title')?.textContent || '';
            const link = item.querySelector('link')?.textContent || '';
            const desc = item.querySelector('description')?.textContent || '';

            // Parse "1. Tesla – 3.30 kr/kWh"
            const rankMatch = titleText.match(/^(\d+)\.\s+(.+?)\s+[–-]\s+([\d.]+)\s+kr\/kWh/);
            const rank = rankMatch ? parseInt(rankMatch[1]) : 0;
            const name = rankMatch ? rankMatch[2] : titleText;
            const price = rankMatch ? parseFloat(rankMatch[3]) : 0;

            // Parse description for best price
            const bestMatch = desc.match(/beste pris:\s*([\d.]+)\s*kr\/kWh/);
            const bestPrice = bestMatch ? parseFloat(bestMatch[1]) : null;

            // Parse availability
            const availMatch = desc.match(/Tilgjengelig:\s*(.+)/);
            const availability = availMatch ? availMatch[1] : '';

            return { rank, name, price, bestPrice, link, availability };
          });

          const buildDate = doc.querySelector('lastBuildDate')?.textContent;
          if (buildDate) {
            setLastUpdated(new Date(buildDate).toLocaleDateString('nb-NO', {
              day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
            }));
          }

          setChargers(data);
          setLoading(false);
          return;
        } catch {
          continue;
        }
      }
      setLoading(false);
    };

    fetchPrices();
  }, []);

  const cardBg = darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-stone-200 shadow-sm';
  const headingColor = darkMode ? 'text-white' : 'text-stone-900';
  const subColor = darkMode ? 'text-gray-400' : 'text-stone-500';
  const priceColor = darkMode ? 'text-teal-400' : 'text-teal-600';
  const bestColor = darkMode ? 'text-emerald-400' : 'text-emerald-600';
  const rowHover = darkMode ? 'hover:bg-slate-800' : 'hover:bg-stone-50';
  const borderColor = darkMode ? 'border-slate-800' : 'border-stone-100';

  return (
    <div className={`min-h-screen pt-24 sm:pt-28 pb-16 px-4 transition-colors duration-200 ${darkMode ? '' : 'bg-stone-50'}`}>
      <SEO
        title="Billigste hurtiglading elbil Norge"
        path="/lading"
        description="Daglig oppdatert oversikt over de billigste hurtigladerne for elbil i Norge. Sammenlign priser per kWh fra Tesla Supercharger, Ionity, Recharge, Mer og flere."
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Billigste hurtiglading for elbil i Norge',
          description: 'Daglig oppdatert liste over de 10 billigste hurtigladerne i Norge, sortert etter pris per kWh.',
          url: 'https://effektnorge.no/lading',
          inLanguage: 'nb-NO',
          mainEntity: {
            '@type': 'ItemList',
            name: 'Topp 10 billigste hurtigladere i Norge',
            numberOfItems: chargers.length,
            itemListElement: chargers.map(c => ({
              '@type': 'ListItem',
              position: c.rank,
              name: `${c.name} hurtiglading`,
              url: c.link
            }))
          }
        }}
      />

      <div className="max-w-3xl mx-auto">
        <header className="mb-8">
          <h1 className={`text-4xl sm:text-5xl font-bold mb-3 ${headingColor}`} style={{ fontFamily: "'Lora', serif" }}>
            Hurtiglading for elbil
          </h1>
          <p className={`text-lg ${subColor}`} style={{ fontFamily: "'Lora', serif" }}>
            De 10 billigste hurtigladerne i Norge — oppdatert daglig
          </p>
          {lastUpdated && (
            <p className={`text-sm mt-2 ${subColor}`}>Sist oppdatert: {lastUpdated}</p>
          )}
        </header>

        {loading && (
          <div className="space-y-3">
            {[...Array(10)].map((_, i) => (
              <div key={i} className={`rounded-xl p-5 border animate-pulse ${cardBg}`}>
                <div className={`h-6 w-2/3 rounded ${darkMode ? 'bg-slate-700' : 'bg-stone-200'}`} />
              </div>
            ))}
          </div>
        )}

        {!loading && chargers.length > 0 && (
          <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
            {chargers.map((charger, i) => (
              <a
                key={charger.rank}
                href={charger.link}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-4 px-5 py-4 transition-colors duration-100 ${rowHover} ${i < chargers.length - 1 ? `border-b ${borderColor}` : ''}`}
                style={{ animation: `fadeIn 0.2s ease-out ${i * 0.03}s both` }}
              >
                <span className={`text-2xl font-bold w-8 text-center flex-shrink-0 ${i === 0 ? priceColor : subColor}`}>
                  {charger.rank}
                </span>
                <div className="flex-1 min-w-0">
                  <span className={`font-semibold block ${headingColor}`}>{charger.name}</span>
                  {charger.availability && (
                    <span className={`text-xs ${subColor}`}>{charger.availability}</span>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <span className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-stone-900'}`}>{charger.price.toFixed(2)} kr</span>
                  <span className={`text-xs block ${subColor}`}>/kWh</span>
                  {charger.bestPrice && (
                    <span className={`text-xs ${bestColor}`}>Beste: {charger.bestPrice.toFixed(2)} kr</span>
                  )}
                </div>
              </a>
            ))}
          </div>
        )}

        <div className={`mt-8 text-center ${subColor}`}>
          <p className="text-sm mb-3">Prisdata fra</p>
          <a
            href="https://rasklading.no"
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-colors duration-150 border ${darkMode ? 'bg-slate-900 border-slate-700 text-teal-400 hover:bg-slate-800' : 'bg-white border-stone-200 text-teal-600 hover:bg-stone-50 shadow-sm'}`}
          >
            Rasklading.no
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className={`text-xs mt-3 ${subColor}`}>Se alle priser, sammenlign operatører og finn nærmeste lader</p>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default Lading;
